import { c as _c } from "react/compiler-runtime";
import { TargetSelector, TargetSelectorUi, type TargetSelectorMode } from '../TargetSelector';
import { useMemo } from 'react';
import { useWippenSupportSpringsRecommendation } from './hooks/use-wippen-support-springs-recommendation';
import { useTranslation } from '@/hooks/use-translation';
import { WippenSupportSpringsDesignMode, type WippenSupportSpringsDesignTarget } from '@/lib/piano/touch-design/design/wippen-support-springs-design';
import { type WippenSupportSpringsDesignProps } from './WippenSupportSpringsDesign.types';
import { SupportSpringBalanceWeightChart } from '@/components/charts/SupportSpringBalanceWeightChart';
import { DesignLayout } from '../DesignLayout';
import { formatGrams, formatSignedGrams } from '@/lib/format/format-weight';
import { useWippenSupportSpringsTargetSelector } from './hooks/use-wippen-support-springs-target-selector';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { WippenDesignSelector } from './wippen-design-selector/WippenDesignSelector';
import { useTargetSeries } from '../hooks/use-target-series';
import { useTargetReading } from '../hooks/use-target-reading';
import { benchAdjustment } from '@/lib/piano/touch-design/bench-adjustment';
export const WippenSupportSpringsDesign: React.FC<WippenSupportSpringsDesignProps> = t0 => {
  const $ = _c(68);
  const {
    analyzedKeyboard
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const {
    wippenSupportSpringsDesignMode,
    wippenSupportSpringsDesignTarget,
    onModeChange,
    onTargetChange,
    updateWippenSupportSpringsDesign
  } = useWippenSupportSpringsTargetSelector(analyzedKeyboard);
  useWippenSupportSpringsRecommendation(wippenSupportSpringsDesignMode, wippenSupportSpringsDesignTarget, updateWippenSupportSpringsDesign);
  const {
    supportSpringBalanceWeightTargetSerie: targetSerie
  } = useTargetSeries();
  let t1;
  if ($[0] !== analyzedKeyboard || $[1] !== targetSerie) {
    t1 = {
      difference: benchAdjustment,
      format: formatGrams,
      formatDifference: formatSignedGrams,
      keyboard: analyzedKeyboard,
      targetSerie
    };
    $[0] = analyzedKeyboard;
    $[1] = targetSerie;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderReading = useTargetReading(t1);
  let t2;
  if ($[3] !== t) {
    t2 = t("Choose this option if your piano does not have wippen support springs.");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("Do not use wippen support springs");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector as const,
      target: null
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3) {
    t5 = {
      description: t2,
      label: t3,
      options: t4,
      value: WippenSupportSpringsDesignMode.None
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t) {
    t6 = t("All your wippen support springs will have the same tension.");
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("Use wippen support springs with constant tension");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      componentFactory: _temp,
      selectorUi: TargetSelectorUi.CustomComponentTargetSelector as const
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t6 || $[17] !== t7) {
    t9 = {
      description: t6,
      label: t7,
      options: t8,
      value: WippenSupportSpringsDesignMode.Constant
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("The general idea is to define a base weight that will decrease to zero starting from a specific key.");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("Use wippen support springs with smooth transition");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      componentFactory: _temp2,
      selectorUi: TargetSelectorUi.CustomComponentTargetSelector as const
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t11) {
    t13 = {
      description: t10,
      label: t11,
      options: t12,
      value: WippenSupportSpringsDesignMode.SmoothTransition
    };
    $[24] = t10;
    $[25] = t11;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t || $[28] !== useSupportSpringMeasurements) {
    t14 = useSupportSpringMeasurements ? t("Use wippen support springs measured values") : t("Requires the \u201CMeasure Wippen Support Spring tension\u201D option.");
    $[27] = t;
    $[28] = useSupportSpringMeasurements;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = !useSupportSpringMeasurements;
  let t16;
  if ($[30] !== t) {
    t16 = t("Keep the wippen support springs as is.");
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector as const,
      target: null
    };
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t14 || $[34] !== t15 || $[35] !== t16) {
    t18 = {
      description: t14,
      isDisabled: t15,
      label: t16,
      options: t17,
      value: WippenSupportSpringsDesignMode.AsMeasured
    };
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t13 || $[38] !== t18 || $[39] !== t5 || $[40] !== t9) {
    t19 = [t5, t9, t13, t18];
    $[37] = t13;
    $[38] = t18;
    $[39] = t5;
    $[40] = t9;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const targetSelectorModes = t19;
  let t20;
  if ($[42] !== targetSerie.data) {
    t20 = targetSerie.data.filter(_temp3);
    $[42] = targetSerie.data;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const sprungPoints = t20;
  let t21;
  if ($[44] !== sprungPoints) {
    t21 = sprungPoints.length === 0 ? null : {
      count: sprungPoints.length,
      first: sprungPoints[0].number,
      last: sprungPoints[sprungPoints.length - 1].number
    };
    $[44] = sprungPoints;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const sprungKeys = t21;
  let t22;
  if ($[46] !== t) {
    t22 = t("Target");
    $[46] = t;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== onModeChange || $[49] !== onTargetChange || $[50] !== t22 || $[51] !== targetSelectorModes || $[52] !== wippenSupportSpringsDesignMode || $[53] !== wippenSupportSpringsDesignTarget) {
    t23 = <TargetSelector title={t22} modes={targetSelectorModes} selectedMode={wippenSupportSpringsDesignMode} selectedTarget={wippenSupportSpringsDesignTarget} onModeChange={onModeChange} onTargetChange={onTargetChange} />;
    $[48] = onModeChange;
    $[49] = onTargetChange;
    $[50] = t22;
    $[51] = targetSelectorModes;
    $[52] = wippenSupportSpringsDesignMode;
    $[53] = wippenSupportSpringsDesignTarget;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t) {
    t24 = t("Target");
    $[55] = t;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t24) {
    t25 = [{
      colorClassName: "text-ptl-series-target",
      id: "target",
      label: t24,
      mark: "line-thick" as const
    }];
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== analyzedKeyboard || $[60] !== renderReading || $[61] !== sprungKeys || $[62] !== t25 || $[63] !== targetSerie) {
    t26 = <SupportSpringBalanceWeightChart renderReading={renderReading} variant="design" keyboard={analyzedKeyboard} sprungKeys={sprungKeys} targetSerie={targetSerie} extraLegendItems={t25} />;
    $[59] = analyzedKeyboard;
    $[60] = renderReading;
    $[61] = sprungKeys;
    $[62] = t25;
    $[63] = targetSerie;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t23 || $[66] !== t26) {
    t27 = <DesignLayout objective={t23} chart={t26} />;
    $[65] = t23;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  return t27;
};
function _temp(currentTarget, onTargetChange_0) {
  return <WippenDesignSelector currentTarget={currentTarget} onTargetChange={onTargetChange_0} shouldEnableTensionDropIndexSelector={false} />;
}
function _temp2(currentTarget_0, onTargetChange_1) {
  return <WippenDesignSelector currentTarget={currentTarget_0} onTargetChange={onTargetChange_1} shouldEnableTensionDropIndexSelector={true} />;
}
function _temp3(point) {
  return point.payload !== undefined && point.payload !== null;
}